import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import {
  CategoricalSharePayload,
  CategoricalSharePlotView,
} from "@digital-twins/deck-ui/plots/categorical-share";
import {
  ChoiceBarsPlotData,
  ChoicePlotView,
  donutAlternative,
} from "@digital-twins/deck-ui/plots/choice-bars";
import {
  HeatGridPlotView,
  HeatGridPlotViewProps,
} from "@digital-twins/deck-ui/plots/heat-grid";
import { ShareUnit } from "@digital-twins/deck-ui/plots/_shared";
import { FIGURE_VIEWS } from "@/features/studies/report/figures/QuestionFigureView";
import { getAppChartPalette } from "@/lib/charts/chartjs";
import { rankingChartLabelsForLanguage } from "@/lib/charts/rankingChartLabels";

import { AnalystChartPlot } from "@digital-twins/shared-types";

interface AnalystPlotProps {
  plot: AnalystChartPlot;
  index: number;
  isLegacy: boolean;
  /** `chartType: donut` on choice bars with their donut alternative: open on the donut. */
  opensOnDonut?: boolean;
  /** Test id prefix, so a featured table's plot never shares ids with a chart at the same index. */
  testIdScope?: string;
  /** The unit shares print in where the kind has one (a room chart prints counts). Default percent. */
  shareUnit?: ShareUnit;
}

/** Kinds drawn by the plot library directly; the rest go through the survey tab's kind views. */
const LIBRARY_KINDS = new Set(["choice-bars", "heat-grid", "grouped-bars", "stacked-100-bars"]);

/** True when the analyst tab has a view for this plot kind. */
export function hasAnalystPlotView(kind: string): boolean {
  return LIBRARY_KINDS.has(kind) || kind in FIGURE_VIEWS;
}

/**
 * One analyst chart's plot (plots plan T), drawn through the kind's view: the
 * choice bars with their bars / donut toggle, the heat grid, the grouped bars,
 * and every other kind through the survey tab's kind map (`FIGURE_VIEWS`), so
 * a kind lights up here the day its view lands. Figures are Python's; the
 * chrome words the plot prints came stamped in the viewer's UI language.
 */
export function AnalystPlot(props: AnalystPlotProps) {
  const { plot, index, isLegacy, opensOnDonut = false, testIdScope = "analyst", shareUnit } = props;

  const { t, i18n } = useTranslation("app");
  // App-drawn chrome speaks the viewer's UI language.
  const rankingLabels = useMemo(
    () => rankingChartLabelsForLanguage(i18n.language),
    [i18n.language],
  );

  const testId = isLegacy ? `${testIdScope}-plot-${index}-legacy` : `${testIdScope}-plot-${index}`;

  if (plot.kind === "choice-bars") {
    return (
      <div data-testid={testId}>
        <ChoicePlotView
          data={plot.data as unknown as ChoiceBarsPlotData}
          donut={donutAlternative(plot.alternatives)}
          initialMode={opensOnDonut ? "share-donut" : "choice-bars"}
          toggleLabels={{
            bars: t("studyReport.plotToggle.bars"),
            donut: t("studyReport.plotToggle.donut"),
          }}
          locale={i18n.language}
          shareUnit={shareUnit}
          testIdPrefix={`${testIdScope}-choice-plot-${index}`}
        />
      </div>
    );
  }
  if (plot.kind === "heat-grid") {
    return (
      <div data-testid={testId}>
        <HeatGridPlotView
          plot={plot as unknown as HeatGridPlotViewProps["plot"]}
          testIdPrefix={`${testIdScope}-heat-grid-${index}`}
        />
      </div>
    );
  }
  if (plot.kind === "grouped-bars" || plot.kind === "stacked-100-bars") {
    return (
      <div data-testid={testId}>
        <CategoricalSharePlotView
          plot={plot as unknown as CategoricalSharePayload}
          size="card"
          locale={i18n.language}
          shareUnit={shareUnit}
          testIdPrefix={`${testIdScope}-categorical-plot-${index}`}
        />
      </div>
    );
  }
  const View = FIGURE_VIEWS[plot.kind];
  if (!View) {
    return null;
  }
  return (
    <div data-testid={testId}>
      <View
        question={{ questionId: `analyst-chart-${index}`, metricIds: [], kind: plot.kind, plots: { total: plot } }}
        panels={[
          {
            group: { id: "total", sourceId: null, label: null, kind: "total", n: 0, lowBase: false, drawn: true },
            plot,
          },
        ]}
        isCut={false}
        colors={getAppChartPalette()}
        locale={i18n.language}
        rankingLabels={rankingLabels}
        isLegacy={isLegacy}
        initialShareUnit={shareUnit}
      />
    </div>
  );
}
